import { useEffect, useRef, useState } from "react";
import { Badge, Button, Card, OverlayTrigger, Tooltip, ProgressBar, Modal, Collapse } from "react-bootstrap";
import { useNavigate, useSearchParams } from "react-router-dom";

import { Job } from "@models/job.model";
import { JobService } from "@services/jobs/jobService";

import EmptyDataMessage from "src/components/common/EmptyDataMessage";
import Loader from "src/components/common/loader/loader";
import { Paths } from "src/paths";
import { formatLongDate } from "src/shared/utils/common";

import { usePaginator } from "@components/paginator/usePaginator";
import Paginator from "@components/paginator/Paginator";

import { useDocumentTitle } from "@/shared/hooks/useDocumentTitle";
import "./styles.scss";

const JOB_STATUSES = [
    "PENDING", "ACTIVE", "FAILED", "COMPLETED",
    "COMPLETED_WITH_ERRORS", "WARNING", "CANCELLED",
] as const;

export default function JobsView() {
    useDocumentTitle("Jobs");
    const service = new JobService();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();

    const [jobs, setJobs] = useState<Job[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [expandedJobs, setExpandedJobs] = useState<Set<number>>(new Set());

    const initialLimit = Number(searchParams.get("limit")) || 10;
    const initialOffset = Number(searchParams.get("offset")) || 0;
    const paginator = usePaginator({ initialLimit, initialOffset });

    // One box over engagement, campaign, network, job type, status, job number and
    // batch (MPN-1636). Held in the URL so a link to a search is shareable -- triage
    // happens over Slack and "look at job 31402" should be a link.
    const [search, setSearch] = useState<string>(searchParams.get("search") ?? "");
    const [statusFilter, setStatusFilter] = useState<string>(searchParams.get("status") ?? "");
    // Only the operation rows (MPN-1892): a 300-file reprocess is one parent job, and
    // that is the row that gets lost among the standalone jobs that ran around it.
    const [parentsOnly, setParentsOnly] = useState<boolean>(searchParams.get("parents") === "1");
    // What the list was last asked for. The typed search lands here after a pause;
    // the fetch keys on this, not on the keystrokes.
    const [applied, setApplied] = useState({ search, status: statusFilter, parentsOnly });

    const [showConfirm, setShowConfirm] = useState(false);
    const [pendingJobId, setPendingJobId] = useState<number | null>(null);
    const [isRetrying, setIsRetrying] = useState(false);

    // Load expanded jobs from URL on mount
    useEffect(() => {
        const expandedParam = searchParams.get('expanded');
        if (expandedParam) {
            const expandedIds = expandedParam.split(',').map(id => parseInt(id, 10)).filter(id => !isNaN(id));
            setExpandedJobs(new Set(expandedIds));
        }
    }, []);

    // Update URL whenever expandedJobs changes
    useEffect(() => {
        if (expandedJobs.size > 0) {
            const expandedArray = Array.from(expandedJobs);
            const newSearchParams = new URLSearchParams(searchParams);
            newSearchParams.set('expanded', expandedArray.join(','));
            setSearchParams(newSearchParams, { replace: true });
        } else {
            const newSearchParams = new URLSearchParams(searchParams);
            newSearchParams.delete('expanded');
            setSearchParams(newSearchParams, { replace: true });
        }
    }, [expandedJobs]);

    // Debounced: the box refetches as it is typed, and a job number is short enough
    // that firing per keystroke would run four searches to answer one. A change in
    // the filters starts over from the first page.
    useEffect(() => {
        const unchanged =
            applied.search === search &&
            applied.status === statusFilter &&
            applied.parentsOnly === parentsOnly;
        if (unchanged) return;
        const timer = setTimeout(() => {
            setApplied({ search, status: statusFilter, parentsOnly });
            paginator.events.setOffset(0);
        }, 300);
        return () => clearTimeout(timer);
    }, [search, statusFilter, parentsOnly]);

    // The one place the list is fetched, and the URL written to match it. Paging and
    // filters used to each fetch on their own, so opening the page ran the request
    // twice and the slower response won (MPN-1892).
    useEffect(() => {
        const params = new URLSearchParams(searchParams);
        params.set("limit", String(paginator.state.limit));
        params.set("offset", String(paginator.state.offset));
        applied.search ? params.set("search", applied.search) : params.delete("search");
        applied.status ? params.set("status", applied.status) : params.delete("status");
        applied.parentsOnly ? params.set("parents", "1") : params.delete("parents");
        setSearchParams(params, { replace: true });
        void fetchJobs();
    }, [paginator.state.limit, paginator.state.offset, applied]);

    async function openJobDetail(jobId: number) {
        // Carry the list's own state along so the detail page can hand it back. Filters,
        // paging and search all live in the query string already; dropping them here is
        // what made "Back to Jobs" a fresh unfiltered page.
        const query = searchParams.toString();
        navigate(
            `${import.meta.env.BASE_URL}${Paths.JOBS}/${jobId}${query ? `?${query}` : ""}`,
        );
    }

    // Which request is current. Typing "31" then "314" sends two, and the first can
    // land last; a response that is not from the latest request is dropped.
    const latestRequest = useRef(0);

    async function fetchJobs() {
        const request = ++latestRequest.current;
        setIsLoading(true);
        try {
            const list = await service.listJobs(paginator.state.limit, paginator.state.offset, {
                search: applied.search || undefined,
                statuses: applied.status ? [applied.status] : undefined,
                parentsOnly: applied.parentsOnly || undefined,
            });
            if (request !== latestRequest.current) return;
            paginator.events.setSizeOfCurrentPage(list.length);
            setJobs(list);
        } finally {
            if (request === latestRequest.current) setIsLoading(false);
        }
    }

    async function retryJob(jobId: number) {
        try {
            setIsRetrying(true);
            await service.retryJob(jobId);
            await fetchJobs();
        } finally {
            setIsRetrying(false);
            setShowConfirm(false);
            setPendingJobId(null);
        }
    }

    function openConfirm(jobId: number) {
        setPendingJobId(jobId);
        setShowConfirm(true);
    }

    function closeConfirm() {
        if (!isRetrying) {
            setShowConfirm(false);
            setPendingJobId(null);
        }
    }

    function toggleJobExpansion(jobId: number) {
        setExpandedJobs((prev) => {
            const next = new Set(prev);
            if (next.has(jobId)) {
                next.delete(jobId);
            } else {
                next.add(jobId);
            }
            return next;
        });
    }

    const statusBg = (status: string) => {
        const s = status?.toLowerCase?.() || "";
        if (s === "failed" || s === "error") return "danger";
        if (s === "completed" || s === "success") return "success";
        if (s === "running" || s === "in_progress" || s === "processing") return "primary";
        if (s === "created" || s === "queued" || s === "pending" || s === "warning") return "warning";
        return "secondary";
    };

    const clamp = (n: number, min = 0, max = 100) => Math.max(min, Math.min(max, n));

    // The server's two live statuses. A retry is a second copy of the same work under the
    // same parent, so on a running job it runs twice (MPN-2235); the server refuses it too.
    const canRetry = (status: string) => !["pending", "active"].includes(status?.toLowerCase?.() || "");

    const isInProgress = (status: string) => {
        const s = status?.toLowerCase?.() || "";
        return ["running", "in_progress", "processing", "queued", "pending", "started"].includes(s);
    };

    const progressVariant = (pct: number, status: string) => {
        const s = status?.toLowerCase?.() || "";
        if (s === "failed" || s === "error") return "danger";
        if (s === "completed" || s === "success") return "success";
        if (s === "warning") return "warning";
        if (pct >= 66) return "primary";
        if (pct >= 33) return "info";
        return "secondary";
    };

    const getLastErrorFromJob = (job: Job): string | null => {
        if (!job.job_context?.traces || job.job_context.traces.length === 0) return null;
        
        // Find the last trace with an error
        const tracesWithErrors = job.job_context.traces.filter(
            (trace: any) => trace.error_detail && trace.error_detail.trim()
        );
        
        if (tracesWithErrors.length === 0) return null;
        
        const lastErrorTrace = tracesWithErrors[tracesWithErrors.length - 1];
        const lines = lastErrorTrace.error_detail.split('\n').filter((line: string) => line.trim());
        return lines[lines.length - 1] || null;
    };

    const renderJobRow = (job: Job, depth: number = 0) => {
        const isExpanded = expandedJobs.has(job.id);
        const hasChildren = job.children && job.children.length > 0;

        const pctRaw = (job as any).percent_complete as number | undefined;
        const hasPct = typeof pctRaw === "number" && !Number.isNaN(pctRaw);
        const pct = hasPct ? clamp(Math.round(pctRaw!)) : 0;

        const paddingLeft = depth * 40;
        const lastError = getLastErrorFromJob(job);

        const depthClass = depth > 0 ? `jobs-view__row--depth-${Math.min(depth, 2)}` : '';

        return (
            <div key={job.id}>
                {/* Job Row */}
                <div className={`jobs-view__row ${depthClass} px-2`}>
                    {/* Expand/Collapse Icon with indentation */}
                    <div 
                        className="jobs-view__chevron-container" 
                        style={{ width: `${paddingLeft + 30}px`, paddingLeft: `${paddingLeft}px` }}
                    >
                        {hasChildren ? (
                            <Button
                                variant="link"
                                size="sm"
                                className="p-0 text-muted"
                                onClick={() => toggleJobExpansion(job.id)}
                            >
                                <i className={`bi bi-chevron-${isExpanded ? "down" : "right"} fs-16`}></i>
                            </Button>
                        ) : (
                            <div style={{ width: "16px" }}></div>
                        )}
                    </div>

                    {/* Job Type and Error */}
                    <div className="jobs-view__content">
                        <div className="text-truncate">
                            <strong>{job.name || job.type}</strong>
                        </div>
                        {/* The job number, beside the name it belongs to (MPN-1636).
                            The search box already offered "job or batch #" and the row
                            already showed the batch, so a job number was searchable but
                            never readable -- you had to open the job to learn what to
                            type. Here rather than in the context block because that block
                            is hidden under 1200px, and an id you cannot see on a laptop
                            is the problem this is fixing. */}
                        <small className="text-muted">
                            <span className="jobs-view__job-id">job {job.id}</span>
                            {formatLongDate(job.update_date)}
                        </small>
                        {lastError && (
                            <small className="text-danger font-monospace text-truncate d-block jobs-view__error-text">
                                {lastError}
                            </small>
                        )}
                    </div>

                    {/* Which engagement / campaign / network this job is for (MPN-1636).
                        Plenty of job types belong to none of them; those say so with the
                        job type rather than three blanks, which reads as broken. */}
                    <div className="jobs-view__context">
                        {job.engagement_name || job.campaign_name || job.network ? (
                            <>
                                <span className="jobs-view__context-cell text-truncate" title={job.engagement_name ?? ""}>
                                    {job.engagement_name ?? "—"}
                                </span>
                                <span className="jobs-view__context-cell text-truncate" title={job.campaign_name ?? ""}>
                                    {job.campaign_name ?? "—"}
                                </span>
                                <span className="jobs-view__context-cell jobs-view__context-cell--network">
                                    {job.network ? job.network : "—"}
                                </span>
                            </>
                        ) : (
                            <span className="jobs-view__context-cell jobs-view__context-cell--type text-truncate" title={job.type}>
                                {job.type.replace(/_/g, " ").toLowerCase()}
                            </span>
                        )}
                        {job.batch_id != null && (
                            <span className="jobs-view__batch" title={`Batch ${job.batch_id}`}>
                                batch {job.batch_id}
                            </span>
                        )}
                    </div>

                    {/* Progress Bar */}
                    <div className="jobs-view__progress-bar">
                        {hasPct ? (
                            <OverlayTrigger placement="top" overlay={<Tooltip>{`${pct}%`}</Tooltip>}>
                                <ProgressBar
                                    now={pct}
                                    variant={progressVariant(pct, job.status)}
                                    striped={isInProgress(job.status)}
                                    animated={isInProgress(job.status)}
                                />
                            </OverlayTrigger>
                        ) : (
                            <span className="text-muted">—</span>
                        )}
                    </div>

                    {/* Status Badge */}
                    <div className="jobs-view__status-badge">
                        <Badge bg={statusBg(job.status)}>
                            {job.status}
                        </Badge>
                    </div>

                    {/* Actions */}
                    <div className="jobs-view__actions">
                        <OverlayTrigger placement="top" overlay={<Tooltip>View Details</Tooltip>}>
                            <Button variant="outline-secondary" size="sm" onClick={() => openJobDetail(job.id)}>
                                <i className="bi bi-eye-fill"></i>
                            </Button>
                        </OverlayTrigger>
                        {canRetry(job.status) && (
                            <OverlayTrigger placement="top" overlay={<Tooltip>Retry Job</Tooltip>}>
                                <Button variant="outline-secondary" size="sm" aria-label="Retry job"
                                        onClick={() => openConfirm(job.id)}>
                                    <i className="bi bi-arrow-repeat"></i>
                                </Button>
                            </OverlayTrigger>
                        )}
                    </div>
                </div>

                {/* Expanded Content: Child Jobs Only */}
                <Collapse in={isExpanded}>
                    <div>
                        {hasChildren && (
                            <div>
                                {job.children.map((child) => renderJobRow(child, depth + 1))}
                            </div>
                        )}
                    </div>
                </Collapse>
            </div>
        );
    };

    return (
        <div className="px-3">
            <Card className="custom-card mt-3">
                <Card.Header className="card-header justify-content-between">
                    <Card.Title>Jobs</Card.Title>
                    <div className="d-flex align-items-center gap-2 jobs-view__filters">
                        <input
                            type="search"
                            className="form-control form-control-sm"
                            placeholder="Search by engagement, campaign, network, type, status, job or batch #"
                            aria-label="Search jobs"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                        />
                        <select
                            className="form-select form-select-sm w-auto"
                            aria-label="Filter by status"
                            value={statusFilter}
                            onChange={(e) => setStatusFilter(e.target.value)}
                        >
                            <option value="">All statuses</option>
                            {JOB_STATUSES.map((s) => (
                                <option key={s} value={s}>{s.replace(/_/g, " ").toLowerCase()}</option>
                            ))}
                        </select>
                        <div className="form-check form-check-sm text-nowrap">
                            <input
                                type="checkbox"
                                className="form-check-input"
                                id="jobs-parents-only"
                                checked={parentsOnly}
                                onChange={(e) => setParentsOnly(e.target.checked)}
                            />
                            <label className="form-check-label" htmlFor="jobs-parents-only">
                                Parents only
                            </label>
                        </div>
                        <Button variant="primary" size="sm" onClick={fetchJobs}>
                            <i className="bi bi-arrow-clockwise" />
                        </Button>
                    </div>
                </Card.Header>

                <Card.Body className="p-0">
                    {isLoading && (
                        <div className="p-4">
                            <Loader />
                        </div>
                    )}
                    {!isLoading && jobs.length < 1 && (
                        <div className="p-4">
                            <EmptyDataMessage />
                        </div>
                    )}

                    {!isLoading && jobs.length > 0 && (
                        <div>
                            {jobs.map((job) => renderJobRow(job, 0))}
                        </div>
                    )}
                </Card.Body>

                <Card.Footer>
                    <Paginator paginator={paginator} />
                </Card.Footer>
            </Card>

            <Modal show={showConfirm} onHide={closeConfirm} centered>
                <Modal.Header closeButton>
                    <Modal.Title>
                        <i className="bi bi-exclamation-triangle-fill me-2" />
                        Confirm Retry
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>Retry job {pendingJobId ? `#${pendingJobId}` : ""}?</Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={closeConfirm} disabled={isRetrying}>
                        Cancel
                    </Button>
                    <Button
                        variant="danger"
                        onClick={() => pendingJobId && retryJob(pendingJobId)}
                        disabled={isRetrying}
                    >
                        {isRetrying ? "Retrying…" : "Retry"}
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
}